import { atom } from 'jotai';
import type { ParsedUrlQuery } from 'querystring';
import { AUTH_VARIANT_PARAM } from '@/utils/pages/auth-variant';
import { atomWithNextRouter } from './atomWithNextRouter';

/**
 * Reactive atom for Next.js router query parameters
 * Uses atomWithNextRouter with Router.events subscription for proper reactivity
 * The atomWithNextRouter handles SSR safety internally
 */
const locationAtom = atomWithNextRouter();

/**
 * Internal query parameters that should not be visible in the URL
 * These are used for passing context between middleware and pages
 */
const INTERNAL_QUERY_PARAMS = [
  '_page_type',
  '_original_url',
  // Middleware's auth cache-key marker. Without this it survives into the
  // address bar on the first sort/filter change, and a shared or bookmarked
  // copy of that URL forces `no-store` for everyone who opens it.
  AUTH_VARIANT_PARAM,
];

/**
 * Derived atom that provides just the query parameters
 */
export const queryParamsAtom = atom(
  // Read query params from location
  (get) => {
    const location = get(locationAtom);
    const query: ParsedUrlQuery = {};

    // Convert URLSearchParams to ParsedUrlQuery
    location.searchParams?.forEach((value, key) => {
      if (query[key]) {
        // Handle multiple values for same key
        if (Array.isArray(query[key])) {
          (query[key] as string[]).push(value);
        } else {
          query[key] = [query[key] as string, value];
        }
      } else {
        query[key] = value;
      }
    });

    return query;
  },
  // Write query params by updating location
  (get, set, newQuery: ParsedUrlQuery) => {
    const location = get(locationAtom);
    const newSearchParams = new URLSearchParams();

    // Convert ParsedUrlQuery to URLSearchParams, filtering out internal params
    Object.entries(newQuery).forEach(([key, value]) => {
      // Skip internal parameters when writing to URL
      if (INTERNAL_QUERY_PARAMS.includes(key)) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((v) => newSearchParams.append(key, v));
      } else if (value) {
        newSearchParams.set(key, value);
      }
    });

    set(locationAtom, {
      ...location,
      searchParams: newSearchParams,
    });
  }
);
